<template>
	<LimboSearch
		v-if="search && search.endpointUrl"
		ref="search"
		v-slot="limboSearchBindings"
		tag="article"
		class="c-search-list-layout"
		:search-filters="search"
		:config="{
			...computedSearchConfig,
		}"
		v-on="$listeners"
		@update="onSearchUpdate"
	>
		<PageHeader
			ref="header"
			:intro="{ ...intro, image: null }"
			:header-graphic="headerGraphic"
			layout="list"
			class="z-20 relative"
		>
			<!-- Custom header content -->
			<template v-if="$scopedSlots.customHeader" #custom>
				<slot
					name="customHeader"
					v-bind="{
						...limboSearchBindings,
						groupData: null,
						...slotBindings,
					}"
				></slot>
			</template>

			<!-- Area for the search fields in the header -->
			<div v-if="$scopedSlots.searchFields" role="search" class="w-full">
				<slot
					name="searchFields"
					v-bind="{
						...limboSearchBindings,
						groupData: null,
						...slotBindings,
					}"
				></slot>
			</div>
		</PageHeader>

		<LongReadController
			v-if="!hideSearch || checkIfSearching(limboSearchBindings)"
			id="long-read-controller"
			:key="$route.path"
			v-slot="longReadController"
		>
			<!-- The sticky top-bar used on grouped searches -->
			<div
				v-if="$scopedSlots.stickyBar || isGroupedSearch"
				class="c-search-list-layout__sticky w-full sticky z-10"
			>
				<slot
					name="stickyBar"
					v-bind="{
						...limboSearchBindings,
						groupData: null,
						...slotBindings,
					}"
				>
					<div
						class="
							flex
							justify-between
							items-center
							>=1024:gap-x-16
							px-layout-margin
							py-16
							bg-white
						"
					>
						<!-- Sticky status heading -->
						<div
							role="status"
							class="
								text-label-sm
								font-medium
								uppercase
								flex-shrink-0
								w-1/12col
								min-w-fit
								mr-auto
							"
						>
							{{
								limboSearchBindings.state.isLoading
									? computedLabels.searching || 'Søger...'
									: statusText
							}}
						</div>

						<!-- TOC links -->
						<SearchListLayoutToc
							:key="$route.path"
							class="
								transform
								translate-x-layout-margin
								>=1024:translate-x-0
							"
							v-bind="longReadController"
						>
							<div
								class="
									w-layout-margin
									flex-shrink-0
									>=1024:hidden
								"
							></div>
						</SearchListLayoutToc>

						<!-- Layout picker in sticky on desktop -->
						<div
							v-if="showLayoutPicker"
							class="
								hidden
								>=1024:flex
								ml-auto
								w-1/12col
								min-w-fit
								self-start
								flex-shrink-0
								items-center
								gap-4xs/h
							"
							:class="{
								'<768:hidden': hideLayoutPickerOnMobile,
							}"
						>
							<BaseButton
								tag="label"
								aria-label="Skift til flisevisning"
								class="
									relative
									w-40
									>=768:w-44
									h-40
									>=768:h-44
									p-0
									border border-theme
								"
								:class="{
									'bg-transparent hover:bg-theme hover:bg-opacity-10 text-theme opacity-50':
										pickedLayout !== 'grid',
								}"
							>
								<input
									v-model="pickedLayout"
									type="radio"
									name="layout-switch-1"
									class="
										appearance-none
										absolute
										left-0
										top-0
										w-full
										h-full
									"
									value="grid"
								/>
								<SvgIconGrid class="w-14 h-14" />
							</BaseButton>

							<BaseButton
								tag="label"
								aria-label="Skift til listevisning"
								class="
									relative
									w-40
									>=768:w-44
									h-40
									>=768:h-44
									p-0
									border border-theme
								"
								:class="{
									'bg-transparent hover:bg-theme hover:bg-opacity-10 text-theme opacity-50':
										pickedLayout !== 'list',
								}"
							>
								<input
									v-model="pickedLayout"
									type="radio"
									name="layout-switch-1"
									class="
										appearance-none
										absolute
										left-0
										top-0
										w-full
										h-full
									"
									value="list"
								/>
								<SvgIconList class="w-18 h-14" />
							</BaseButton>
						</div>
					</div>
				</slot>
			</div>

			<!-- Active filters -->
			<ul
				v-if="activeFilters.length"
				class="px-layout-margin py-2xs/v w-full flex flex-wrap gap-8"
			>
				<li key="ryd alle">
					<BaseButton
						class="px-12 py-8"
						@click="() => clearActiveFilters()"
					>
						{{ search.labels.clearFilters }}
					</BaseButton>
				</li>

				<li v-for="filter in activeFilters" :key="filter.value">
					<BaseButton
						class="
							px-12
							py-8
							text-theme
							border border-theme border-opacity-20
							hover:border-opacity-40
							focus:border-opacity-40
							bg-transparent
							hover:bg-theme-10
							focus:bg-theme-10
						"
						:aria-label="
							search.labels.clearFilters + ' ' + filter.label
						"
						@click="() => clearActiveFilters([filter])"
					>
						<AreaIcon
							v-if="filter.iconUrl"
							class="flex-shrink-0 flex-grow-0 w-18 h-18 mr-8"
							:src="filter.iconUrl"
						/>

						<span v-text="filter.label"></span>

						<SvgIconClose
							class="flex-shrink-0 flex-grow-0 ml-12 w-10"
						/>
					</BaseButton>
				</li>
			</ul>

			<!-- The search content -->
			<div
				role="region"
				aria-label="Søgeresultater"
				class="w-full px-layout-margin"
				:class="{
					'mt-lg/v': isGroupedSearch,
					'mt-md/v': !isGroupedSearch,
				}"
			>
				<!-- Header when there's ony one group -->
				<slot
					v-if="!isGroupedSearch"
					name="searchHeader"
					v-bind="{
						...limboSearchBindings,
						groupData: null,
						...slotBindings,
					}"
				>
					<header
						v-if="statusText || showLayoutPicker"
						class="w-full flex items-center"
					>
						<div
							v-if="
								limboSearchBindings.state.isLoading ||
								!limboSearchBindings.state.isInitiated
							"
							role="status"
							class="text-search-heading mr-4xs/h"
						>
							{{ computedLabels.searching || 'Søger...' }}
						</div>
						<div
							v-else-if="statusText"
							role="status"
							class="text-search-heading mr-4xs/h"
							:class="{
								'text-theme': !showingAllResults,
							}"
							v-text="statusText"
						></div>

						<div
							v-if="showLayoutPicker"
							class="
								ml-auto
								self-start
								flex-shrink-0 flex
								items-center
								gap-4xs/h
							"
							:class="{
								'<768:hidden': hideLayoutPickerOnMobile,
							}"
						>
							<BaseButton
								tag="label"
								aria-label="Skift til flisevisning"
								class="
									relative
									w-40
									>=768:w-44
									h-40
									>=768:h-44
									p-0
									border border-theme
								"
								:class="{
									'bg-transparent hover:bg-theme hover:bg-opacity-10 text-theme opacity-50':
										pickedLayout !== 'grid',
								}"
							>
								<input
									v-model="pickedLayout"
									type="radio"
									name="layout-switch-2"
									class="
										appearance-none
										absolute
										left-0
										top-0
										w-full
										h-full
									"
									value="grid"
								/>
								<SvgIconGrid class="w-14 h-14" />
							</BaseButton>

							<BaseButton
								tag="label"
								aria-label="Skift til listevisning"
								class="
									relative
									w-40
									>=768:w-44
									h-40
									>=768:h-44
									p-0
									border border-theme
								"
								:class="{
									'bg-transparent hover:bg-theme hover:bg-opacity-10 text-theme opacity-50':
										pickedLayout !== 'list',
								}"
							>
								<input
									v-model="pickedLayout"
									type="radio"
									name="layout-switch-2"
									class="
										appearance-none
										absolute
										left-0
										top-0
										w-full
										h-full
									"
									value="list"
								/>
								<SvgIconList class="w-18 h-14" />
							</BaseButton>
						</div>
					</header>
				</slot>
				<!-- No results on grouped searches -->
				<header
					v-else-if="
						limboSearchBindings.state.isInitiated &&
						!limboSearchBindings.state.isLoading &&
						!resultTotal
					"
					class="w-full flex items-center"
				>
					<div
						role="status"
						class="text-search-heading mr-4xs/h"
						v-text="
							computedLabels.noResults ||
							'Din søgning gav ingen resultater.'
						"
					></div>
				</header>

				<div
					v-for="(group, groupIndex) in limboSearchBindings.data &&
					limboSearchBindings.data.groups"
					:id="`search-group-${group.id}`"
					:key="groupIndex"
					class="isolate"
					:class="{
						'mt-80': groupIndex,
					}"
				>
					<slot
						v-if="isGroupedSearch"
						name="searchHeader"
						v-bind="{
							...limboSearchBindings,
							groupData: group,
							...slotBindings,
						}"
					>
						<header
							v-if="group.title || group.name"
							class="relative w-full flex items-center"
						>
							<LongReadTarget
								v-if="
									limboSearchBindings.data.groups.length > 1
								"
								class="z-10"
								:container-id="`search-group-${group.id}`"
								:link-to-toc="{
									name: `Spring til &quot;${
										group.title || group.name
									}&quot; i resultatnavigationen`,
									attrs: {
										'data-focus-ignore': true,
									},
									setHash: false,
								}"
								:title="group.title || group.name"
								:meta-data="{
									total: group.total || 0,
								}"
							/>
							<BaseH2
								class="text-search-heading mr-4xs/h"
								v-text="group.title || group.name"
							/>

							<div
								v-if="showLayoutPicker && groupIndex === 0"
								class="
									flex
									>=1024:hidden
									ml-auto
									self-start
									flex-shrink-0
									items-center
									gap-4xs/h
								"
								:class="{
									'<768:hidden': hideLayoutPickerOnMobile,
								}"
							>
								<BaseButton
									tag="label"
									aria-label="Skift til flisevisning"
									class="
										relative
										w-40
										>=768:w-44
										h-40
										>=768:h-44
										p-0
										border border-theme
									"
									:class="{
										'bg-transparent hover:bg-theme hover:bg-opacity-10 text-theme opacity-50':
											pickedLayout !== 'grid',
									}"
								>
									<input
										v-model="pickedLayout"
										type="radio"
										name="layout-switch-3"
										class="
											appearance-none
											absolute
											left-0
											top-0
											w-full
											h-full
										"
										value="grid"
									/>
									<SvgIconGrid class="w-14 h-14" />
								</BaseButton>

								<BaseButton
									tag="label"
									aria-label="Skift til listevisning"
									class="
										relative
										w-40
										>=768:w-44
										h-40
										>=768:h-44
										p-0
										border border-theme
									"
									:class="{
										'bg-transparent hover:bg-theme hover:bg-opacity-10 text-theme opacity-50':
											pickedLayout !== 'list',
									}"
								>
									<input
										v-model="pickedLayout"
										type="radio"
										name="layout-switch-3"
										class="
											appearance-none
											absolute
											left-0
											top-0
											w-full
											h-full
										"
										value="list"
									/>
									<SvgIconList class="w-18 h-14" />
								</BaseButton>
							</div>
						</header>
					</slot>

					<slot
						name="searchContent"
						v-bind="{
							...limboSearchBindings,
							groupData: group,
							...slotBindings,
						}"
					>
						<div
							v-if="pickedLayout === 'grid'"
							role="region"
							aria-label="Resultater"
							class="
								w-full
								mt-xs/v
								grid
								gap-x-sm/h gap-y-md/v
								grid-cols-1
								>=768:grid-cols-2
								>=1024:grid-cols-3
								transition-opacity
								duration-200
							"
							:class="{
								'opacity-80':
									limboSearchBindings.state.isLoading,
							}"
						>
							<template v-for="item in group.items">
								<CardItemEmployee
									v-if="item.template === 'subsiteEmployee'"
									v-bind="item"
									:id="`bookmark-${
										typeof group.id === 'undefined'
											? 0
											: group.id
									}-${item.id}`"
									:key="`${groupIndex}-${
										item.key || item.id
									}-employee`"
									:heading-level-offset="group.title ? 2 : 1"
									@click.native.capture="
										() => setFromHash(group.id, item.id)
									"
								/>
								<CardItem
									v-else
									v-bind="item"
									:id="`bookmark-${
										typeof group.id === 'undefined'
											? 0
											: group.id
									}-${item.id}`"
									:key="`${groupIndex}-${
										item.key || item.id
									}`"
									:heading-level-offset="group.title ? 2 : 1"
									@click.native.capture="
										() => setFromHash(group.id, item.id)
									"
								/>
							</template>
						</div>
						<div
							v-if="pickedLayout === 'list'"
							:key="groupIndex"
							role="region"
							aria-label="Resultater"
							class="
								w-full
								mt-xs/v
								grid
								gap-12
								grid-cols-1
								transition-opacity
								duration-200
							"
							:class="{
								'opacity-80':
									limboSearchBindings.state.isLoading,
							}"
						>
							<template v-for="item in group.items">
								<ListItemEvent
									v-if="item.template === 'eventPage'"
									v-bind="item"
									:id="`bookmark-${
										typeof group.id === 'undefined'
											? 0
											: group.id
									}-${item.id}`"
									:key="`${groupIndex}-${
										item.key || item.id
									}-event`"
									:heading-level-offset="group.title ? 2 : 1"
									@click.native.capture="
										() => setFromHash(group.id, item.id)
									"
								/>
								<ListItemProject
									v-else-if="
										item.template === 'subsiteProject'
									"
									v-bind="item"
									:id="`bookmark-${
										typeof group.id === 'undefined'
											? 0
											: group.id
									}-${item.id}`"
									:key="`${groupIndex}-${
										item.key || item.id
									}-project`"
									:heading-level-offset="group.title ? 2 : 1"
									@click.native.capture="
										() => setFromHash(group.id, item.id)
									"
								/>
								<template
									v-else-if="
										item.template === 'subsiteEmployee'
									"
								>
									<!--
										Employees use the same card on mobile
									-->
									<ListItemEmployee
										v-bind="item"
										:id="`bookmark-${
											typeof group.id === 'undefined'
												? 0
												: group.id
										}-${item.id}-1`"
										:key="`${groupIndex}-${
											item.key || item.id
										}-employee-list`"
										:heading-level-offset="
											group.title ? 2 : 1
										"
										class="<768:hidden"
										@click.native.capture="
											() => setFromHash(group.id, item.id)
										"
									/>
									<CardItemEmployee
										v-bind="item"
										:id="`bookmark-${
											typeof group.id === 'undefined'
												? 0
												: group.id
										}-${item.id}-2`"
										:key="`${groupIndex}-${
											item.key || item.id
										}-employee-card`"
										:heading-level-offset="
											group.title ? 2 : 1
										"
										class=">=768:hidden"
										@click.native.capture="
											() => setFromHash(group.id, item.id)
										"
									/>
								</template>
								<ListItem
									v-else
									v-bind="item"
									:id="`bookmark-${
										typeof group.id === 'undefined'
											? 0
											: group.id
									}-${item.id}`"
									:key="`${groupIndex}-${
										item.key || item.id
									}`"
									:heading-level-offset="group.title ? 2 : 1"
									@click.native.capture="
										() => setFromHash(group.id, item.id)
									"
								/>
							</template>
						</div>
					</slot>

					<slot
						name="searchFooter"
						v-bind="{
							...limboSearchBindings,
							groupData: group,
							...slotBindings,
						}"
					>
						<div
							v-if="
								limboSearchBindings.state.hasMoreItems &&
								(!isGroupedSearch ||
									limboSearchBindings.state.hasMoreItems[
										group.id
									])
							"
							class="
								mt-md/v
								flex
								justify-center
								items-center
								transition-opacity
								duration-200
							"
							:class="{
								'opacity-80':
									limboSearchBindings.state.isLoading,
								'-mb-40':
									groupIndex !==
									limboSearchBindings.data.groups.length - 1,
							}"
						>
							<button
								class="
									group
									inline-flex
									items-center
									flex-none
									text-body-sm
								"
								@click="
									() =>
										(limboSearchBindings.state.isLoading
											? () => {}
											: limboSearchBindings.action[
													fetchMoreFunction
											  ])(group.id)
								"
							>
								<div
									class="
										w-48
										h-48
										flex
										justify-center
										items-center
										text-text
										bg-text bg-opacity-10
										group-hover:bg-opacity-5
										rounded-full
										transition-colors
										duration-200
									"
								>
									<SvgIconWedge class="w-14 h-8 -mb-1" />
								</div>

								<span
									class="ml-1em"
									v-text="loadMoreText(group.total || 0)"
								></span>
							</button>
						</div>
					</slot>
				</div>
			</div>

			<!-- Whatever else we would ever like below the search -->
			<slot
				v-bind="{
					...limboSearchBindings,
					groupData: null,
					...slotBindings,
				}"
			></slot>
		</LongReadController>
	</LimboSearch>
	<!-- Else some weird error may happen on navigation -->
	<div
		v-else
		class="c-search-list-layout c-search-list-layout--no-endpoint"
	></div>
</template>

<script>
import LimboSearch from '~/components/shared/LimboSearch';
import PageHeader from '~/components/main/PageHeader';
import AreaIcon from '~/components/main/AreaIcon';
import {
	LongReadController,
	LongReadTarget,
} from '~/components/shared/LongRead';
import SearchListLayoutToc from '~/components/main/SearchListLayoutToc';
import CardItem from '~/components/main/CardItem';
import CardItemEmployee from '~/components/main/CardItemEmployee';
import ListItem from '~/components/main/ListItem';
import ListItemEvent from '~/components/main/ListItemEvent';
import ListItemEmployee from '~/components/main/ListItemEmployee';
import ListItemProject from '~/components/main/ListItemProject';
import SvgIconClose from '~/assets/svgs/icon-close.svg?inline';
import SvgIconGrid from '~/assets/svgs/icon-grid.svg?inline';
import SvgIconList from '~/assets/svgs/icon-list.svg?inline';
import SvgIconWedge from '~/assets/svgs/icon-wedge.svg?inline';
import { cookieInformationData } from '~/components/shared/CookieInformation';

const sessionSearchLayout = new Map();
export default {
	name: 'SearchListLayout',

	components: {
		LimboSearch,
		PageHeader,
		AreaIcon,
		LongReadController,
		LongReadTarget,
		SearchListLayoutToc,
		CardItem,
		CardItemEmployee,
		ListItem,
		ListItemEvent,
		ListItemEmployee,
		ListItemProject,
		SvgIconClose,
		SvgIconGrid,
		SvgIconList,
		SvgIconWedge,
	},
	inheritAttrs: false,

	props: {
		intro: Object,
		headerGraphic: {
			type: Number,
			default: -2,
		},

		search: {
			type: Object,
			default: () => ({
				endpointUrl: '',
				fields: [],
			}),
		},
		searchConfig: {
			type: Object,
			default: () => ({}),
		},
		searchArea: {
			type: Object,
			default: () => ({
				singular: 'resultat',
				plural: 'resultater',
			}),
		},
		fetchMoreFunction: {
			type: String,
			default: 'fetchMore',
		},

		layout: {
			type: String,
			default: 'dual',
			validator(value) {
				return ['dual', 'list', 'grid'].includes(value);
			},
		},
		defaultLayout: {
			type: String,
			default: 'grid',
			validator(value) {
				return ['list', 'grid'].includes(value);
			},
		},
		hideLayoutPickerOnMobile: {
			type: Boolean,
			default: false,
		},
		defaults: {
			type: Object,
			default: () => ({}),
		},

		hideOnNoSearch: {
			type: Boolean,
			default: false,
		},
	},

	data() {
		const defaultParameterValues = {};
		(this.search?.fields || []).forEach((field) => {
			// Add the value
			if ('value' in field) {
				defaultParameterValues[field.name] = field.value;
			} else if (field?.items?.length) {
				const items = field.items.filter((item) => item.checked);
				if (items.length) {
					defaultParameterValues[field.name] = items.map(
						(item) => item.value
					);
				}
			} else {
				// Add a value
				this.$set(field, 'value', '');
			}

			// Add the default value
			if ('defaultValue' in field) {
				defaultParameterValues[field.name] = field.defaultValue;
			}
		});
		Object.assign(defaultParameterValues, this.defaults);

		const defaultLayout =
			sessionSearchLayout.get(defaultParameterValues.pageId) ||
			this.$cookie.get(`${defaultParameterValues.pageId}SearchLayout`) ||
			this.$cookie.get('SearchLayout') ||
			this.defaultLayout ||
			'grid';
		return {
			defaultParameterValues,
			pickedLayout: this.layout === 'dual' ? defaultLayout : this.layout,
			resultTotal: 0,
			nonGroupedTitle: '',
			removeHashOnSearch: false,
		};
	},

	computed: {
		computedSearchConfig() {
			const config = {
				defaultParameterValues: this.defaultParameterValues,
				allowSearchFiltersMutation: true,
				updateVueRouteOnSearch: true,
				immediateSearch:
					this.hideOnNoSearch && !this.checkIfSearching()
						? false
						: { useUrlQuery: true },
				...this.searchConfig,
			};
			config.dataOutputTransformerMethod = this.isGroupedSearch
				? (data) => {
						const groups = (data?.groups || []).filter(
							(group) => group?.items?.length
						);
						return { ...data, groups };
				  }
				: (data, response) => {
						this.nonGroupedTitle = '';
						if (data) {
							this.nonGroupedTitle = response?.data?.title || '';
							return {
								groups: [
									{
										items: data,
										...(response?.data?.pagination || {}),
									},
								],
							};
						}
						return { groups: [] };
				  };
			return config;
		},
		showLayoutPicker() {
			return this.layout === 'dual';
		},

		fieldsByName() {
			return (this.search?.fields || []).reduce((acc, field) => {
				acc[field.name] = field;
				return acc;
			}, {});
		},
		fieldsByType() {
			return (this.search?.fields || []).reduce((acc, field) => {
				acc[field.type] = acc[field.type] || [];
				acc[field.type].push(field);
				return acc;
			}, {});
		},

		computedLabels() {
			const labels = this.search?.labels || {};
			for (const key in labels) {
				if (!labels[key] || labels[key].indexOf('[') === 0) {
					delete labels[key];
				}
			}
			return labels;
		},
		statusText() {
			if (this.computedLabels.results) {
				return this.computedLabels.results;
			}

			if (this.nonGroupedTitle) {
				return this.nonGroupedTitle;
			}

			if (!this.showingAllResults) {
				if (this.resultTotal === 1) {
					return this.isGroupedSearch
						? 'Resultat'
						: `${this.resultTotal} resultat matcher din søgning`;
				}
				if (this.resultTotal) {
					return this.isGroupedSearch
						? 'Resultater'
						: `${this.resultTotal} resultater matcher din søgning`;
				}
				return this.isGroupedSearch
					? 'Ingen resultater'
					: 'Ingen resultater matcher din søgning';
			}
			return this.isGroupedSearch
				? 'Resultater'
				: 'Viser alle ' + this.searchArea.plural;
		},

		activeFilters() {
			return (this.search?.fields || [])
				.filter((field) => field.items && field.value)
				.flatMap((field) =>
					field.items.filter((item) =>
						field.value.split(',').includes(item.value)
					)
				);
		},
		showingAllResults() {
			const filtered = (this.search?.fields || []).find(
				(field) =>
					field.value !== this.defaultParameterValues[field.name] &&
					!field.value !== !this.defaultParameterValues[field.name]
			);
			return !filtered;
		},

		isGroupedSearch() {
			return !!this.searchConfig?.enableGroupedSearch;
		},
		hasExtendedFilters() {
			return (
				'includePhrase' in this.fieldsByName ||
				'includeWords' in this.fieldsByName ||
				'excludeWords' in this.fieldsByName
			);
		},
		slotBindings() {
			return {
				layout: this.pickedLayout,
				statusText: this.statusText,
				fieldsByName: this.fieldsByName,
				fieldsByType: this.fieldsByType,
				hasExtendedFilters: this.hasExtendedFilters,
				showingAllResults: this.showingAllResults,
			};
		},

		hideSearch() {
			if (this.hideOnNoSearch) {
				return true;
			}
			return false;
		},
	},

	watch: {
		layout(value) {
			if (value !== 'dual') {
				this.pickedLayout = value;
			}
		},
		pickedLayout(value) {
			if (cookieInformationData?.categories?.functional) {
				this.$cookie.set('SearchLayout', value);
				this.$cookie.set(
					`${this.defaultParameterValues.pageId}SearchLayout`,
					value
				);
			}
			sessionSearchLayout.set(this.defaultParameterValues.pageId, value);
		},
	},

	beforeMount() {
		if (!cookieInformationData?.categories?.functional) {
			if (
				this.$cookie.get(
					`${this.defaultParameterValues.pageId}SearchLayout`
				)
			) {
				this.$cookie.remove(
					`${this.defaultParameterValues.pageId}SearchLayout`
				);
			}
			if (this.$cookie.get('SearchLayout')) {
				this.$cookie.remove('SearchLayout');
			}
		}
	},

	methods: {
		loadMoreText(total = 0) {
			return (this.computedLabels.loadMore || 'Vis flere (i alt {total})')
				.split('{total}')
				.join(total);
		},
		clearActiveFilters(filters = this.activeFilters) {
			const affectedFilters = new Set();
			filters.forEach((item) => {
				const owner = (this.search?.fields || []).find((field) =>
					field.items?.includes(item)
				);
				this.$set(item, 'checked', false);
				if (owner) {
					affectedFilters.add(owner);
				}
			});
			affectedFilters.forEach((field) => {
				const value = field.items
					.filter((item) => item.checked)
					.map((item) => item.value)
					.join(',');
				this.$set(field, 'value', value);
			});
		},

		onSearchUpdate(e) {
			if (!e?.state?.isLoading) {
				const hashId = this.$route.hash.split('#').join('');
				if (this.removeHashOnSearch) {
					history.replaceState(
						{},
						'',
						window.location.pathname + window.location.search
					);
					this.removeHashOnSearch = false;
				} else if (!e?.state?.isAppend && hashId) {
					this.$nextTick(() => {
						const el =
							this.$el.querySelector(`#${hashId}`) ||
							this.$el.querySelector(`[id^='${hashId}-']`);
						el?.scrollIntoView?.();
					});
				}
			} else if (e?.state?.hasFetchedOnce) {
				this.removeHashOnSearch = true;
			}

			this.resultTotal =
				this.$refs.search?.searchData?.pagination?.total || 0;
			if (this.isGroupedSearch) {
				this.resultTotal = 0;
				for (const groupKey in this.$refs.search?.searchData
					?.pagination || {}) {
					const group =
						this.$refs.search.searchData.pagination[groupKey];
					const { total = 0 } = group;
					this.resultTotal += +total;
				}
			}
		},
		setFromHash(groupId = 0, itemId = 0) {
			history.replaceState({}, '', `#bookmark-${groupId}-${itemId}`);
		},

		checkIfSearching(limboSearchBindings) {
			let _return = false;
			if (limboSearchBindings) {
				if (
					!limboSearchBindings.state.hasFetchedOnce ||
					!limboSearchBindings.data ||
					!limboSearchBindings.query.parameters
				) {
					return false;
				}

				for (const key in this.defaultParameterValues) {
					if (
						key in limboSearchBindings.query.parameters &&
						this.defaultParameterValues[key] !==
							limboSearchBindings.query.parameters[key]
					) {
						_return = true;
						break;
					}
				}
			} else {
				for (const key in this.defaultParameterValues) {
					if (
						key in this.$route.query &&
						this.defaultParameterValues[key] !==
							this.$route.query[key]
					) {
						_return = true;
						break;
					}
				}
			}
			return _return;
		},
	},
};
</script>

<style lang="postcss">
.c-search-list-layout:before {
	@apply bg-gray-light;
	content: '';
	display: block;
	position: fixed;
	left: -50vw; /* for some reason blur triggers weird stuff if just 0 0 and 100vw and vh */
	top: 0;
	width: 200vw;
	height: calc(
		100vh + 9999999px
	); /* and for some reason the blur cuts the bottom of the page if just 100vh / treats it as semi-absolute? */
	z-index: -1;
}

.c-search-list-layout__sticky {
	top: var(--site-header-height, 0px);

	&:before {
		@apply absolute bg-white;
		content: '';
		left: calc(var(--margin, 0px) * -1);
		top: 0;
		width: calc(100% + var(--margin, 0px) * 2);
		height: 100%;
		z-index: -1;
		box-shadow: 0px 4px 24px rgba(0, 0, 0, 0.08);
	}

	& * {
		scroll-margin-top: var(--site-header-height, 0px);
	}
}

@screen >=1441 {
	.c-search-list-layout__sticky {
		--margin: (
				var(--visual-viewport-width, 100vw) -
					var(--theme-layout-max, 1440px)
			) / 2;
	}
}
</style>
